import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import ws from '../services/wsService';

interface InternalMessage {
  id?: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    avatar?: string;
  };
  senderLine: number;
  content: string;
  timestamp: string;
}

interface InternalChatProps {
  ticketId: string;
  supportLine: number;
}

const InternalChat: React.FC<InternalChatProps> = ({ ticketId, supportLine }) => {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();

    const handleInternalMessage = (data: any) => {
      if (data.ticketId === ticketId) {
        setMessages(prev => [...prev, {
          sender: data.senderInfo,
          senderLine: data.message.senderLine,
          content: data.message.content,
          timestamp: data.message.timestamp,
        }]);
      }
    };

    ws.on('internal_message', handleInternalMessage);

    return () => {
      ws.off('internal_message', handleInternalMessage);
    };
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/tickets/${ticketId}/internal-messages`);
      if (response.data.success) {
        setMessages(response.data.data.messages || []);
      }
    } catch (error: any) {
      if (error.response?.status !== 403) {
        toast.error('Ошибка загрузки внутренней переписки');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      return;
    }

    try {
      setSending(true);
      const response = await api.post(`/api/tickets/${ticketId}/internal-message`, {
        content: newMessage.trim()
      });

      if (response.data.success) {
        setNewMessage('');
        // Сообщение будет добавлено через WebSocket
        fetchMessages();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при отправке сообщения');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex flex-col h-full border border-border rounded-lg bg-card">
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-medium text-foreground">
          Внутренняя переписка между линиями поддержки
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Сообщения видны только операторам и администраторам
        </p>
      </div>

      {/* Сообщения */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[500px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-muted-foreground">Загрузка сообщений...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Нет внутренних сообщений</p>
              <p className="text-xs text-muted-foreground mt-1">
                Начните переписку с другой линией поддержки
              </p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            const isCurrentUser = user?.id === message.sender.id;
            const isLine1 = message.senderLine === 1;
            const isLine2 = message.senderLine === 2;

            return (
              <div
                key={index}
                className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    isCurrentUser
                      ? 'bg-primary text-primary-foreground'
                      : isLine1
                      ? 'bg-muted text-foreground'
                      : 'bg-blue-500/20 text-foreground border border-blue-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">
                      {message.sender.firstName} {message.sender.lastName}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isLine1 
                        ? 'bg-blue-500/30 text-blue-700 dark:text-blue-300' 
                        : 'bg-purple-500/30 text-purple-700 dark:text-purple-300'
                    }`}>
                      Линия {message.senderLine}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Форма отправки */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Введите сообщение для другой линии поддержки..."
            rows={2}
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
          />
          <Button
            type="submit"
            disabled={sending || !newMessage.trim()}
            size="icon"
            className="self-end"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Вы отправляете сообщение от линии {user?.supportLine || 1}
        </p>
      </form>
    </div>
  );
};

export default InternalChat;

